import 'twin.macro'
import type { Transition, Variants } from 'framer-motion'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'

import type { AsideAndMainProps, AsideProps, MainProps } from './types'

type ContextType = {
  mobileAsideOpen?: boolean
}

const Context = React.createContext<ContextType>({ mobileAsideOpen: undefined })

export function AsideAndMain({ children, mobileAsideOpen }: AsideAndMainProps) {
  const value = React.useMemo(
    () => ({
      mobileAsideOpen,
    }),
    [mobileAsideOpen],
  )

  return (
    <Context.Provider value={value}>
      <div tw="h-screen bg-default-subdued overflow-hidden flex">
        {children}
      </div>
    </Context.Provider>
  )
}

const variants: Variants = {
  closed: { x: '-100%' },
  open: { x: 0 },
}

const transition: Transition = { type: 'spring', bounce: 0, duration: 0.4 }

export const Aside = ({ children, innerStyles, ...props }: AsideProps) => {
  const contextValue = React.useContext(Context)

  return (
    <>
      {contextValue.mobileAsideOpen !== undefined && (
        <div tw="md:hidden relative z-10">
          <AnimatePresence>
            {contextValue.mobileAsideOpen && (
              <motion.div
                key="sidebar"
                initial="closed"
                exit="closed"
                animate="open"
                variants={variants}
                transition={transition}
                tw="fixed top-0 left-0 w-full max-w-full md:max-w-md h-full"
              >
                <aside tw="h-full" {...props}>
                  <div
                    tw="w-full flex flex-col flex-grow overflow-y-auto border-solid border-0 border-r border-gray-200 p-10"
                    css={innerStyles}
                  >
                    {children}
                  </div>
                </aside>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      <aside tw="hidden md:flex md:flex-shrink-0" {...props}>
        <div
          tw="w-72 flex flex-col flex-grow overflow-y-auto border-solid border-0 border-r border-gray-200 p-10"
          css={innerStyles}
        >
          {children}
        </div>
      </aside>
    </>
  )
}

export const Main = ({ children, innerStyles, ...props }: MainProps) => {
  return (
    <main tw="w-full overflow-y-auto" {...props}>
      <div tw="w-full max-w-2xl mx-auto p-4 md:p-10" css={innerStyles}>
        {children}
      </div>
    </main>
  )
}
